import React, {FC} from 'react';
import {View, Text, ScrollView, StyleSheet, Image, DimensionValue} from 'react-native';
import CustomModal from '../../CustomModal/CustomModal';
import {useTheme} from '~contexts/ThemeContext';
import {AffectedOrder} from '~types/subscription/subscription.types';
import moment from 'moment-timezone';
import ModernButton from '~components/ModernButton/ModernButton';
import {spacing, borderRadius} from '~constants/design';

interface IProps {
  isVisible: boolean;
  onClose: () => void;
  onConfirm: () => void;
  affectedOrders: AffectedOrder[];
  isCancelling?: boolean;
}

const AffectedOrdersModal: FC<IProps> = ({
  isVisible,
  onClose,
  onConfirm,
  affectedOrders,
  isCancelling = false,
}) => {
  const {colors} = useTheme();
  const styles = createStyles(colors);

  return (
    <CustomModal
      modalIsVisible={isVisible}
      onCloseModal={onClose}
      height="60%"
      backgroundColor={colors.surface}
    >
      <View style={styles.container}>
        <Text style={styles.title}>Cancel Subscription</Text>
        <Text style={styles.warningText}>
          These event bookings will be cancelled as they are outside your current plan and you are cancelling your future subscription. Are you sure you want to cancel?
        </Text>
        
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
          {affectedOrders.map((order) => (
            <View key={order.order_id} style={styles.orderItem}>
              {order.event.image_url ? (
                <Image source={{uri: order.event.image_url}} style={styles.eventImage} />
              ) : (
                <View style={[styles.eventImage, {backgroundColor: colors.gray200}]} />
              )}
              <View style={styles.orderInfo}>
                <Text style={styles.eventName} numberOfLines={1}>{order.event.name}</Text>
                <Text style={styles.eventDate}>
                  {moment(order.event.start_date).format('MMM DD, YYYY • HH:mm')}
                </Text>
              </View>
            </View>
          ))}
        </ScrollView>

        <View style={styles.buttonContainer}>
          <ModernButton
            title="No, Keep Subscription"
            onPress={onClose}
            variant="outline"
            style={styles.button}
            disabled={isCancelling}
          />
          <ModernButton
            title="Yes, Cancel"
            onPress={onConfirm}
            variant="danger"
            style={styles.button}
            loading={isCancelling}
          />
        </View>
      </View>
    </CustomModal>
  );
};

const createStyles = (colors: any) => StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: 12,
    textAlign: 'center',
  },
  warningText: {
    fontSize: 15,
    color: colors.text,
    marginBottom: 20,
    lineHeight: 22,
    textAlign: 'center',
  },
  scrollView: {
    flex: 1,
    marginBottom: 20,
  },
  scrollContent: {
    paddingBottom: 10,
  },
  orderItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    backgroundColor: colors.surfaceVariant || colors.gray100,
    borderRadius: 12,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: colors.border,
  },
  eventImage: {
    width: 44,
    height: 44,
    borderRadius: 8,
    marginRight: 12,
  },
  orderInfo: {
    flex: 1,
  },
  eventName: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.text,
  },
  eventDate: {
    fontSize: 13,
    color: colors.gray600,
    marginTop: 2,
  },
  buttonContainer: {
    gap: 12,
  },
  button: {
    width: '100%',
  },
});

export default AffectedOrdersModal;
